import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * <PhotoLightbox>
 *
 * A shared full-size photo viewer used everywhere a checklist / form
 * thumbnail can be clicked. Extracted from Maintenance.js so all
 * check-out / check-in / pre-trip flows can reuse the same viewer.
 *
 * Controlled component — parent owns `photos` (array of URL strings),
 * the currently-shown `index`, and `onClose`. Keyboard support
 * (Esc / arrows) is wired here so every caller inherits it.
 *
 * Props:
 *   - open:      boolean — is the lightbox visible
 *   - photos:    string[] — full list of URLs; `photos[index]` is shown
 *   - index:     number   — currently-shown index (0-based)
 *   - onClose:   () => void
 *   - onNavigate: (newIndex: number) => void — parent updates index
 */
export default function PhotoLightbox({ open, photos = [], index = 0, onClose, onNavigate }) {
  const step = (dir) => {
    if (photos.length <= 1) return;
    let next = index + dir;
    if (next < 0) next = photos.length - 1;
    if (next >= photos.length) next = 0;
    onNavigate?.(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, index, photos.length]);

  if (!open) return null;
  const photo = photos[index];
  if (!photo) return null;

  // Fix (2026-08-30): Radix Dialog sets `body { pointer-events: none }`
  // while open, restoring it only on its own portalled content. This
  // component used to render as a normal in-tree child, so it silently
  // inherited pointer-events:none whenever opened from inside a Dialog
  // (Maintenance/Checkout/Checkin/Pre-Trip) — the X button, click-
  // outside, and prev/next were all dead to mouse/touch; only the
  // Escape-key handler above still worked. Portalling to document.body
  // + explicit `pointer-events-auto` on every interactive element fixes
  // that regardless of which Dialog (if any) is currently open.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center pointer-events-auto"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 pointer-events-auto"
        onClick={onClose}
        data-testid="photo-lightbox-close"
        aria-label="Close photo viewer"
      >
        <X className="w-8 h-8" />
      </button>

      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
          {index + 1} / {photos.length}
        </div>
      )}

      {photos.length > 1 && (
        <button
          type="button"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 pointer-events-auto"
          onClick={(e) => { e.stopPropagation(); step(-1); }}
          data-testid="photo-lightbox-prev"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      <img
        src={photo}
        alt="Full size"
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      />

      {photos.length > 1 && (
        <button
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 pointer-events-auto"
          onClick={(e) => { e.stopPropagation(); step(1); }}
          data-testid="photo-lightbox-next"
          aria-label="Next photo"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs">
        Tap outside or press Esc to close
      </p>
    </div>,
    document.body
  );
}
